import { defineStore } from 'pinia'
import type { NestedObject } from '@/types'
import { useCoreSocket } from '@/services/socket/core-socket'
import type { Risk } from '@/schemas/general/enums'
import {
  DEFAULT_HOURLY_RATE_ADJUSTMENT,
  DEFAULT_RISK_ADJUSTMENT,
  type Office_Architect_Data,
  type SocketOfficeCalculationResultData,
  zod_Office_Architect_Data,
} from '@arcexperts/core-types'
import { deepClone } from '@/helpers/objects_arrays'

export interface State {
  office: OfficeStateData | null
  previousObject: OfficeStateData | null
  hourlyRate: number
  riskLevel: Risk | null
}

type OfficeStateData = Office_Architect_Data & {
  canton?: string
}

function normalizeOfficeData(data: NestedObject | OfficeStateData | null): OfficeStateData | null {
  if (!data) return null

  const raw = data as Record<string, unknown>
  const normalized = zod_Office_Architect_Data.parse(raw)

  return {
    ...normalized,
    canton: typeof raw.canton === 'string' ? raw.canton : undefined,
  }
}

function buildHourlyRatePayload(
  office: OfficeStateData
): OfficeStateData {
  return office
}

function buildRiskLevelPayload(office: OfficeStateData): Office_Architect_Data {
  return office
}

export const useOfficeCalculator = defineStore('officeCalculator', {
  state: (): State => ({
    office: null,
    previousObject: null,
    hourlyRate: 0,
    riskLevel: null,
  }),
  getters: {
    getOffice: (state) => state.office,
    getHourlyRate: (state) => state.hourlyRate,
    getRiskLevel: (state) => state.riskLevel,
    getSelectedHourlyRate: (state) => {
      if (state.office) {
        const hourlyRateAdjustment = state.office.hourlyRateAdjustment || DEFAULT_HOURLY_RATE_ADJUSTMENT
        const riskAdjustment = state.office.riskAdjustment || DEFAULT_RISK_ADJUSTMENT
        return (
          state.hourlyRate + hourlyRateAdjustment + ((state.hourlyRate + hourlyRateAdjustment) * riskAdjustment) / 100
        )
      }
      return 0
    },
    getAdjustedHourlyRate: (state) => {
      if (state.office) {
        const hourlyRateAdjustment = state.office.hourlyRateAdjustment || DEFAULT_HOURLY_RATE_ADJUSTMENT
        return state.hourlyRate + hourlyRateAdjustment
      }
      return 0
    },
  },
  actions: {
    initOffice(office: NestedObject | OfficeStateData | null): void {
      const normalized = normalizeOfficeData(office)
      this.$patch({ office: normalized, previousObject: deepClone(normalized) })
      this.setHourlyRate()
      this.setRiskLevel()
    },
    setOffice(office: NestedObject | OfficeStateData | null): void {
      const normalized = normalizeOfficeData(office)
      if (!normalized) return

      /* detect which calculation we have to trigger */
      /* update previousObject as not-referenced with JSON */
      if (
        normalized.canton !== this.previousObject?.canton ||
        normalized.foundingYear !== this.previousObject?.foundingYear ||
        normalized.officeSize !== this.previousObject?.officeSize ||
        normalized.internalRoles.toString() !== this.previousObject?.internalRoles.toString()
      ) {
        this.$patch({ office: normalized, previousObject: deepClone(normalized) })
        this.setHourlyRate()
      } else {
        this.$patch({ office: normalized, previousObject: deepClone(normalized) })
        this.setRiskLevel()
      }
    },
    setHourlyRate() {
      if (this.office) {
        useCoreSocket().messageCenter?.triggerOfficeCalculation(
          'architect_office',
          'hourlyRate',
          buildHourlyRatePayload(this.office)
        )
      }
    },
    setRiskLevel() {
      if (this.office) {
        useCoreSocket().messageCenter?.triggerOfficeCalculation(
          'architect_office',
          'riskLevel',
          buildRiskLevelPayload(this.office)
        )
      }
    },
    setResult(result: SocketOfficeCalculationResultData) {
      switch (result.request) {
        case 'hourlyRate':
          this.hourlyRate = result.value
          return
        case 'riskLevel':
          this.riskLevel = result.value
      }
    },
  },
})
